import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CallStageChangeEvent, LiveCallSnapshot } from "@/types";

const MAX_RECENT = 20;

export type LiveCallDialerState = {
  recentCalls: LiveCallSnapshot[];
  currentIndex: number;
  autoFollow: boolean;
  loading: boolean;
  error: string | null;
};

const initialState: LiveCallDialerState = {
  recentCalls: [],
  currentIndex: 0,
  autoFollow: true,
  loading: false,
  error: null,
};

const liveCallDialerSlice = createSlice({
  name: "liveCallDialer",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setRecentCalls(state, action: PayloadAction<LiveCallSnapshot[]>) {
      const sorted = [...action.payload]
        .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
        .slice(0, MAX_RECENT);
      state.recentCalls = sorted;
      if (state.currentIndex >= sorted.length) {
        state.currentIndex = sorted.length > 0 ? 0 : 0;
      }
      state.loading = false;
      state.error = null;
    },
    applyStageChange(state, action: PayloadAction<CallStageChangeEvent>) {
      const evt = action.payload;
      const existingIndex = state.recentCalls.findIndex((c) => c.callId === evt.callId);
      const existing = existingIndex >= 0 ? state.recentCalls[existingIndex] : null;
      const snapshot: LiveCallSnapshot = {
        callId: evt.callId,
        agentId: evt.agentId,
        leadId: evt.leadId,
        leadName: evt.leadName ?? existing?.leadName ?? null,
        contactName: evt.contactName ?? existing?.contactName ?? null,
        contactEmail: evt.contactEmail ?? existing?.contactEmail ?? null,
        contactPhone: evt.contactPhone ?? existing?.contactPhone ?? null,
        companyName: evt.companyName ?? existing?.companyName ?? null,
        number: evt.number ?? existing?.number ?? null,
        processing_stage: evt.processing_stage ?? existing?.processing_stage ?? null,
        current_workflow_node: evt.current_workflow_node ?? existing?.current_workflow_node ?? null,
        stage_history: evt.stage_history ?? existing?.stage_history ?? [],
        updatedAt: Date.now(),
      };

      // Always build a brand-new array reference so any consumer relying on
      // reference equality (useSelector default, React.memo) re-renders.
      const without = existingIndex >= 0
        ? state.recentCalls.filter((_, i) => i !== existingIndex)
        : state.recentCalls;
      const merged = [snapshot, ...without].slice(0, MAX_RECENT);
      const sorted = [...merged].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
      state.recentCalls = sorted;

      if (existingIndex < 0) {
        if (state.autoFollow) {
          state.currentIndex = 0;
        } else if (state.currentIndex < sorted.length - 1) {
          state.currentIndex += 1;
        }
      } else {
        const newIndex = sorted.findIndex((c) => c.callId === evt.callId);
        if (existingIndex === state.currentIndex && newIndex !== state.currentIndex) {
          state.currentIndex = newIndex;
        }
      }
    },
    // Merge in additional fields onto an existing snapshot without touching
    // stage history or processing_stage — used by useLiveCallStages to fill
    // in contact / lead / company names that the backend snapshot didn't
    // include (e.g. when the backend hasn't been rebuilt yet).
    enrichSnapshot(state, action: PayloadAction<{ callId: number; patch: Partial<LiveCallSnapshot> }>) {
      const idx = state.recentCalls.findIndex((c) => c.callId === action.payload.callId);
      if (idx < 0) return;
      // Immutable update — new object at index, new array reference.
      state.recentCalls = state.recentCalls.map((c, i) =>
        i === idx ? { ...c, ...action.payload.patch } : c,
      );
    },
    setCurrentIndex(state, action: PayloadAction<number>) {
      const n = state.recentCalls.length;
      if (n === 0) {
        state.currentIndex = 0;
        return;
      }
      const idx = action.payload;
      state.currentIndex = Math.max(0, Math.min(n - 1, idx));
    },
    next(state) {
      if (state.recentCalls.length === 0) return;
      state.currentIndex = Math.min(state.recentCalls.length - 1, state.currentIndex + 1);
    },
    prev(state) {
      if (state.recentCalls.length === 0) return;
      state.currentIndex = Math.max(0, state.currentIndex - 1);
    },
    setAutoFollow(state, action: PayloadAction<boolean>) {
      state.autoFollow = action.payload;
    },
    clearLiveCalls(state) {
      state.recentCalls = [];
      state.currentIndex = 0;
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setLoading: setLiveCallsLoading,
  setError: setLiveCallsError,
  setRecentCalls,
  applyStageChange,
  enrichSnapshot,
  setCurrentIndex,
  next: nextLiveCall,
  prev: prevLiveCall,
  setAutoFollow,
  clearLiveCalls,
} = liveCallDialerSlice.actions;

export default liveCallDialerSlice.reducer;
